"use client";

import React, { useState } from "react";
import {
	Container,
	Grid,
	Typography,
	TextField,
	Button,
	Paper,
	FormControlLabel,
	Checkbox,
	Box,
	Alert,
	Divider,
} from "@mui/material";
import Swal from "sweetalert2";

// Next.js page: /delete-acc
// Instructions: drop this file in your Next.js project's `pages/` folder as `delete-acc.jsx` (or adapt to app/ route).
// It uses MUI and sweetalert2. Install libs if needed:
// npm install @mui/material @emotion/react @emotion/styled sweetalert2

export default function DeleteAccountPage() {
	const [form, setForm] = useState({ name: "", email: "", reason: "", confirm: false });
	const [error, setError] = useState("");
	const [sending, setSending] = useState(false);

	const developerName = "All Consulting Lascu"; // make sure this matches your Play Store developer name

	function validate() {
		if (!form.name.trim() || !form.email.trim()) return "Numele și emailul sunt obligatorii.";
		// simple email check
		if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) return "Adresa de email nu este validă.";
		if (!form.confirm) return "Trebuie să confirmați că doriți ștergerea contului.";
		return "";
	}

	async function handleSubmit(e) {
		e.preventDefault();
		setError("");
		const v = validate();
		if (v) return setError(v);

		setSending(true);

		// --- IMPORTANT: we intentionally DO NOT call any backend here.
		// This page demonstrates the UI and fulfillment text required by Google Play.
		// Replace the fake submission below with a real POST to your deletion endpoint.

		await new Promise((r) => setTimeout(r, 700));

		setSending(false);

		// Fictitious SweetAlert showing the request was registered
		Swal.fire({
			title: "Cererea a fost înregistrată",
			html: `Mulțumim, <strong>${form.name}</strong>.<br/>Am primit cererea de ștergere pentru contul asociat cu <strong>${form.email}</strong>. Veți primi un răspuns la email în maximum 30 de zile.`,
			icon: "success",
			confirmButtonText: "OK",
		});

		// reset form (optional)
		setForm({ name: "", email: "", reason: "", confirm: false });
	}

	return (
		<Container maxWidth='md' sx={{ py: { xs: 3, md: 6 } }}>
			<Paper elevation={3} sx={{ p: { xs: 2, md: 4 } }}>
				<Typography variant='h4' component='h1' gutterBottom>
					Cerere de ștergere a contului și a datelor
				</Typography>

				<Typography variant='subtitle1' gutterBottom>
					Această pagină vă permite să solicitați ștergerea contului și a datelor asociate aplicației{" "}
					<strong>All Consulting</strong>.
				</Typography>

				<Divider sx={{ my: 2 }} />

				<Grid container spacing={4}>
					<Grid item xs={12} md={6}>
						<Box component='section'>
							<Typography variant='h6'>Pașii pentru ștergerea contului</Typography>
							<ol>
								<li>Completati formularul din dreapta cu numele și emailul folosit în cont.</li>
								<li>Opțional: explicați motivul ștergerii.</li>
								<li>Bifați confirmarea și apăsați butonul "Trimite".</li>
								<li>
									Veți primi un email de confirmare în 3 zile lucrătoare și o confirmare
									finală după procesare.
								</li>
							</ol>

							<Typography variant='h6' sx={{ mt: 2 }}>
								Ce date vor fi șterse
							</Typography>
							<Typography variant='body2'>
								La confirmarea cererii vor fi șterse următoarele date asociate contului dvs.:
							</Typography>
							<ul>
								<li>Date de identificare: nume, adresă de email.</li>
								<li>
									Conţinut adăugat: setări cont, profile, fişiere încărcate şi înregistrări
									legate direct de cont.
								</li>
								<li>
									Date de analiză specifice contului (logs, istoricul activității legat
									exclusiv de contul dvs.).
								</li>
							</ul>

							<Typography variant='h6' sx={{ mt: 2 }}>
								Ce date pot fi păstrate și perioada de retenție
							</Typography>
							<Typography variant='body2'>
								Unele date pot fi păstrate anonim sau pentru motive legale/contabilitate.
								Exemple:
							</Typography>
							<ul>
								<li>
									Înregistrări contabile: păstrate până la 10 ani conform legislației fiscale
									aplicabile.
								</li>
								<li>
									Date anonimizate pentru analiză statistică: păstrate până la 3 ani, fără
									posibilitatea de reidentificare.
								</li>
								<li>
									Copiați de backup neaccesibile public pot rămâne până la 90 de zile din
									motive operaționale, apoi sunt suprascrise.
								</li>
							</ul>

							<Typography variant='body2' sx={{ mt: 2 }}>
								Dacă aveți întrebări suplimentare, ne puteți contacta la:{" "}
								<a href='mailto:contact@all-consulting-lascu.ro'>
									contact@all-consulting-lascu.ro
								</a>
								.
							</Typography>
						</Box>
					</Grid>

					<Grid item xs={12} md={6}>
						<Box component='form' onSubmit={handleSubmit} noValidate>
							{error && (
								<Alert severity='error' sx={{ mb: 2 }}>
									{error}
								</Alert>
							)}

							<TextField
								label='Nume complet'
								fullWidth
								margin='normal'
								value={form.name}
								onChange={(e) => setForm((s) => ({ ...s, name: e.target.value }))}
								required
							/>

							<TextField
								label='Email (folosit în cont)'
								type='email'
								fullWidth
								margin='normal'
								value={form.email}
								onChange={(e) => setForm((s) => ({ ...s, email: e.target.value }))}
								required
							/>

							<TextField
								label='Motivul cererii (opțional)'
								fullWidth
								margin='normal'
								multiline
								minRows={3}
								value={form.reason}
								onChange={(e) => setForm((s) => ({ ...s, reason: e.target.value }))}
							/>

							<FormControlLabel
								control={
									<Checkbox
										checked={form.confirm}
										onChange={(e) =>
											setForm((s) => ({ ...s, confirm: e.target.checked }))
										}
									/>
								}
								label={`Confirm că solicit ștergerea contului asociat cu emailul de mai sus și înțeleg informațiile afișate (${developerName}).`}
								sx={{ mt: 1 }}
							/>

							<Box sx={{ display: "flex", gap: 2, mt: 2 }}>
								<Button type='submit' variant='contained' disabled={sending}>
									{sending ? "Trimitere..." : "Trimite cererea"}
								</Button>
								
							</Box>

			
						</Box>
					</Grid>
				</Grid>
			</Paper>
		</Container>
	);
}
